import { ComponentStyleConfig } from '@chakra-ui/react';
import {
  getColorVar,
  mode,
  StyleFunctionProps,
  SystemStyleObject,
} from '@chakra-ui/theme-tools';

import { merge } from 'lodash';
import { createVariables } from '../../../util';
import { textStyles } from '../../common';

const variables = createVariables<'stroke' | 'line'>();

const tab = (props: StyleFunctionProps) =>
  ({
    borderRadius: '8px',
    boxShadow: `inset 0 0 0 1px var(${variables.stroke})`,
    _hover: {
      background: 'layer.1',
      [variables.stroke]: getColorVar(props.theme, 'border.layer.1'),
    },
    _selected: {
      background: 'none',
      [variables.stroke]: getColorVar(props.theme, 'state.selected'),
    },
    _disabled: {
      '&&': {
        [variables.stroke]: 'transparent',
        background: 'none',
      },
    },
  }) satisfies SystemStyleObject;

export const TabStyles: ComponentStyleConfig = {
  parts: ['tablist', 'tab', 'aftertabs', 'tabpanel'],
  baseStyle: (props) => ({
    tablist: {
      [variables.line]: getColorVar(props.theme, 'border.layer.0'),
      [variables.stroke]: 'transparent',
      '&&': {
        border: 'none',
        m: 0,
      },
      p: props.orientation === 'vertical' ? 2.5 : 0,
      w: 'full',
    },
    tab: {
      '&&': {
        background: 'none',
        color: 'text.placeholder',
        border: 'none',
      },
      '&&&': {
        margin: 0,
      },
      ...(props.orientation === 'vertical' && { justifyContent: 'start' }),
      outline: '2px solid transparent',
      _focusVisible: {
        outlineColor: 'state.selected',
      },
      _hover: {
        color: 'text.secondary',
      },
      _selected: {
        color: 'text.primary',
      },
      _disabled: {
        '&&': {
          color: 'text.disabled',
        },
        cursor: 'not-allowed',
        opacity: 1,
      },
    },
    tabpanel: {
      p: 0,
      height: '100%',
      overflowY: 'auto',
    },
    aftertabs: {
      display: 'none',
    },
  }),
  sizes: {
    sm: {
      tab: {
        ...textStyles['body-sm'],
        py: 1.5,
        px: 2,
      },
    },
    md: {
      tab: {
        px: 3,
        py: 1.5,
        ...textStyles['body-md'],
      },
    },
    lg: {
      tab: {
        px: 4,
        py: 2.5,
        ...textStyles['body-md'],
      },
    },
  },
  variants: {
    // "attached" is the Step Tabs variant
    attached: (props) => ({
      root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      },
      tablist: {
        background: mode('gray.300', 'gray.700')(props),
      },
      tab: {
        px: 8,
        '&&&': {
          color: 'text.primary',
        },
        _hover: {
          background: mode('gray.200', 'gray.800')(props),
        },
        _selected: {
          background: mode('gray.200', 'gray.800')(props),
        },
      },
      tabpanels: {
        flexGrow: 1,
      },
      tabpanel: {
        py: 4,
        px: 6,
        background: mode('gray.200', 'gray.800')(props),
      },
    }),
    'enclosed-colored': (props) => ({
      tablist: {
        p: 0.5,
        boxShadow: `inset 0 0 0 1px var(${variables.line})`,
        borderRadius: '10px',
        ...(props.orientation !== 'vertical' &&
          !props.isFitted && {
            w: 'auto',
            display: 'inline-flex',
          }),
      },
      tab: tab(props),
    }),
    line: (props) => ({
      tablist: {
        backgroundImage:
          props.orientation === 'vertical'
            ? 'none'
            : `linear-gradient(to top, var(${variables.line}) 1px, transparent 1px)`,
      },
      tab: {
        '&&': {
          backgroundImage:
            props.orientation === 'vertical'
              ? `linear-gradient(to right, var(${variables.line}) 1px, transparent 1px)`
              : 'none',
          '&:hover:not(:disabled):not([aria-disabled=true])': {
            backgroundColor: 'tint.hover',
          },
          _selected: {
            backgroundImage: `linear-gradient(to ${props.orientation === 'vertical' ? 'right' : 'top'}, ${getColorVar(props.theme, 'state.selected')} 3px, transparent 3px)`,
            ...(props.orientation === 'vertical' && {
              backgroundColor: 'tint.primary',
              '&:hover:not(:disabled):not([aria-disabled=true])': {
                backgroundColor: `color-mix(in srgb,${getColorVar(props.theme, 'tint.primary')},${getColorVar(props.theme, 'tint.hover')})`,
              },
            }),
          },
        },
      },
    }),
    'solid-rounded': (props) => ({
      tab: merge(tab(props), {
        ...(props.orientation === 'horizontal' && {
          boxShadow: `inset 1px 1px var(${variables.stroke}),inset -1px 1px var(${variables.stroke}),inset 0 -1px var(${variables.line})`,
          _hover: {
            [variables.line]: 'transparent',
          },
          _selected: {
            [variables.line]: 'transparent',
          },
          _disabled: {
            '&&': {
              [variables.line]: 'inherit',
            },
          },
        }),
        borderBottomRadius: props.orientation === 'vertical' ? undefined : 0,
      }),
      aftertabs:
        props.orientation !== 'vertical' && props.isFitted
          ? undefined
          : {
              flex: 1,
              boxShadow: `inset 0 -1px var(${variables.line})`,
            },
    }),
  },
  defaultProps: {
    variant: 'enclosed-colored',
    size: 'md',
  },
};
